import { navigationCache, navigationCacheEnabled, cacheable } from './navigation-cache';
/** Клиент API: один токен на сессию, ошибки — человеческим языком. */

const TOKEN_KEY = 'jungles.token';

export class ApiError extends Error {
  readonly status: number;
  readonly payload: unknown;

  constructor(status: number, message: string, payload?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

let token: string | null = null;
let sessionEpoch=0;
export function getSessionEpoch(){return sessionEpoch;}

export function loadToken(): string | null {
  if (token === null) {
    try {
      token = localStorage.getItem(TOKEN_KEY);
    } catch {
      token = null;
    }
  }
  return token;
}

export function setToken(value: string | null): void {
  sessionEpoch++;
  navigationCache.clear();
  token = value;
  try {
    if (value) localStorage.setItem(TOKEN_KEY, value);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Приватный режим — живём в памяти до перезагрузки.
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  body?: unknown;
  signal?: AbortSignal;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const epoch = sessionEpoch;
  try { return await wireRequest<T>(path, options); }
  finally {
    // A lost response can follow a committed mutation. Revalidate display projections
    // on both success and uncertain failure, never touch pending editor drafts.
    if (navigationCacheEnabled && epoch === sessionEpoch && options.method && options.method !== 'GET' &&
        /^\/api\/(?:me(?:\/|$)|workouts(?:\/|$)|templates(?:\/|$)|nutrition(?:\/|$)|training-plan(?:\/|$))/.test(path)) navigationCache.invalidate();
  }
}

async function wireRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  const auth = loadToken();
  if (auth) headers.Authorization = `Bearer ${auth}`;
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  const response = await fetch(path, {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const message =
      payload && typeof payload === 'object' && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : `Ошибка ${response.status}`;
    throw new ApiError(response.status, message, payload);
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal, force = false): Promise<T> => {
    if (!navigationCacheEnabled || !cacheable(path)) return request<T>(path, { signal });
    // A consumer leaving a route must not abort another consumer's shared read.
    return navigationCache.read(path, () => request<T>(path, {signal: AbortSignal.timeout(12_000)}), force).then(value => {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      return value;
    });
  },
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  del: <T = void>(path: string) => request<T>(path, { method: 'DELETE' }),
};
